import { gsap } from 'gsap';

const EDGE_FALLBACK  = 20;
const HIDE_DELAY     = 900;
const DRAG_THRESHOLD = 4;
const HEADER_OFFSET  = 100;

export function initNumberFacts() {
    document.querySelectorAll('.numberFacts').forEach(el => {
        const scroll = el.querySelector('.numberFacts__scroll');
        const track  = el.querySelector('.numberFacts__track');
        if (!scroll || !track) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let maxScroll = 0;
        let elH       = 0;
        // Sobald der Nutzer selbst scrollt oder zieht, uebersteuert das die
        // Kopplung an den Seiten-Scroll – ab dann bleibt das Modul manuell.
        let userControlled = reduceMotion;

        // Position wird ueber ein Proxy-Objekt getweent und auf scrollLeft geschrieben,
        // damit Scrollleiste, Drag und Auto-Bewegung dieselbe Position teilen
        const pos = { x: 0 };
        const setX = gsap.quickTo(pos, 'x', {
            duration: 0.6,
            ease: 'power3.out',
            onUpdate: () => { scroll.scrollLeft = pos.x; }
        });

        const releaseToUser = () => {
            if (userControlled) return;
            userControlled = true;
            gsap.killTweensOf(pos);
            pos.x = scroll.scrollLeft;
        };

        // Startkante der Items an der Headline (bzw. am umgebenden Container) ausrichten
        const measure = () => {
            const ref = el.querySelector('.numberFacts__headline')
                || el.closest('.container, .container--large, .container--small, .container--xsmall, .container-fluid');

            let edge = EDGE_FALLBACK;
            if (ref) {
                edge = Math.max(0, ref.getBoundingClientRect().left - el.getBoundingClientRect().left);
            }
            el.style.setProperty('--numberFacts-edge', `${Math.round(edge)}px`);

            maxScroll = Math.max(0, scroll.scrollWidth - scroll.clientWidth);
            elH       = el.offsetHeight;
            scroll.classList.toggle('is-scrollable', maxScroll > 1);
        };

        // Horizontale Position aus dem vertikalen Seiten-Scroll ableiten
        const update = () => {
            if (userControlled || maxScroll <= 0) return;

            const rect       = el.getBoundingClientRect();
            const vh         = window.innerHeight;
            const rangeStart = vh - elH;
            const range      = rangeStart - HEADER_OFFSET;
            const progress   = range > 0
                ? gsap.utils.clamp(0, 1, (rangeStart - rect.top) / range)
                : gsap.utils.clamp(0, 1, (vh - rect.top) / (vh - HEADER_OFFSET));

            setX(maxScroll * progress);
        };

        // Scrollleiste nur waehrend der Bewegung einblenden
        let hideTimer = null;
        scroll.addEventListener('scroll', () => {
            scroll.classList.add('is-scrolling');
            clearTimeout(hideTimer);
            hideTimer = setTimeout(() => scroll.classList.remove('is-scrolling'), HIDE_DELAY);
        }, { passive: true });

        // Mit der Maus ziehen, da die Scrollleiste bewusst dezent gehalten ist
        let dragging = false;
        let dragged  = false;
        let startX   = 0;
        let startScrollLeft = 0;

        scroll.addEventListener('pointerdown', e => {
            // Touch laeuft ueber die Richtungserkennung in touchmove,
            // damit vertikales Wischen weiterhin die Seite scrollt
            if (e.pointerType === 'touch') return;

            // Klick auf die Scrollleiste selbst uebergibt die Kontrolle sofort
            if (e.offsetY > scroll.clientHeight) releaseToUser();

            if (e.pointerType !== 'mouse' || e.button !== 0) return;
            dragging = true;
            dragged  = false;
            startX   = e.clientX;
            startScrollLeft = scroll.scrollLeft;
        });

        scroll.addEventListener('pointermove', e => {
            if (!dragging) return;
            const dx = e.clientX - startX;
            if (!dragged) {
                if (Math.abs(dx) < DRAG_THRESHOLD) return;
                dragged = true;
                releaseToUser();
                scroll.classList.add('is-dragging');
                scroll.setPointerCapture(e.pointerId);
            }
            scroll.scrollLeft = startScrollLeft - dx;
            e.preventDefault();
        });

        const endDrag = e => {
            if (!dragging) return;
            dragging = false;
            pos.x = scroll.scrollLeft;
            scroll.classList.remove('is-dragging');
            if (scroll.hasPointerCapture(e.pointerId)) scroll.releasePointerCapture(e.pointerId);
        };

        scroll.addEventListener('pointerup', endDrag);
        scroll.addEventListener('pointercancel', endDrag);

        // Klick nach einem Drag unterdruecken (z.B. auf Links innerhalb der Items)
        scroll.addEventListener('click', e => {
            if (!dragged) return;
            e.preventDefault();
            e.stopPropagation();
        }, true);

        // Nur horizontale Eingaben uebersteuern die Kopplung –
        // vertikales Wischen/Scrollen ueber dem Modul bleibt Seiten-Scroll
        scroll.addEventListener('wheel', e => {
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) releaseToUser();
        }, { passive: true });

        let touchStartX = 0;
        let touchStartY = 0;

        scroll.addEventListener('touchstart', e => {
            if (e.touches.length !== 1) return;
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
        }, { passive: true });

        scroll.addEventListener('touchmove', e => {
            if (userControlled || e.touches.length !== 1) return;
            const dx = Math.abs(e.touches[0].clientX - touchStartX);
            const dy = Math.abs(e.touches[0].clientY - touchStartY);
            if (dx > dy && dx > DRAG_THRESHOLD) releaseToUser();
        }, { passive: true });

        scroll.addEventListener('keydown', e => {
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) releaseToUser();
        });

        measure();
        update();

        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', () => { measure(); update(); }, { passive: true });

        if (window.ResizeObserver) {
            new ResizeObserver(() => { measure(); update(); }).observe(scroll);
        }
    });
}
